import {
  ActionTypes,
  AddMetamodelTypePayload,
  ByIdMetamodelTypePayload,
  EditMetamodelTypePayload,
  EditorErrorPayload,
} from './types';
import { MetamodelEditorLabels } from '../labels';

/**
 * Internal action payload for {@link ActionTypes.EDIT_MODEL_TYPE}. The
 * reducer needs the validation labels so it can populate
 * `editorError.message` with a translated string. We thread them through
 * the action rather than storing labels in reducer state, so a parent
 * re-render with updated `labelOverrides` is reflected immediately
 * (see useReducer initializer caveat).
 */
type EditMetamodelTypeActionPayload = EditMetamodelTypePayload & {
  validationLabels: Pick<
    MetamodelEditorLabels,
    'validationNameEmpty' | 'validationNameTaken'
  >;
};

/* ********************************************************************************
 ---------------------------------  Actions Types  --------------------------------
 **********************************************************************************/

interface EditMetamodelTypeAction {
  type: ActionTypes.EDIT_MODEL_TYPE;
  payload: EditMetamodelTypeActionPayload;
}

interface AddMetamodelTypeAction {
  type: ActionTypes.ADD_MODEL_TYPE;
  payload: AddMetamodelTypePayload;
}

interface DeleteMetamodelTypeAction {
  type: ActionTypes.DELETE_MODEL_TYPE;
  payload: ByIdMetamodelTypePayload;
}
interface SetStateBooleanAction {
  type: ActionTypes.SET_IS_LOADING | ActionTypes.SET_IS_FLEXIBLE;
  payload: { is: boolean };
}
interface EditorErrorAction {
  type: ActionTypes.SET_EDITOR_ERROR;
  payload: EditorErrorPayload;
}
interface SetSelectedMetamodelTypeIdAction {
  type: ActionTypes.SET_SELECTED_NODE_ID;
  payload: Partial<ByIdMetamodelTypePayload>;
}

export type ReducerActions =
  | EditMetamodelTypeAction
  | AddMetamodelTypeAction
  | DeleteMetamodelTypeAction
  | SetStateBooleanAction
  | SetSelectedMetamodelTypeIdAction
  | EditorErrorAction;

/* ********************************************************************************
 --------------------------------  Action Creators  -------------------------------
 **********************************************************************************/

export const editMetamodelTypeAction: (
  payload: EditMetamodelTypeActionPayload
) => EditMetamodelTypeAction = payload => ({
  type: ActionTypes.EDIT_MODEL_TYPE,
  payload,
});
export const addMetamodelTypeAction: (
  payload: AddMetamodelTypePayload
) => AddMetamodelTypeAction = payload => ({
  type: ActionTypes.ADD_MODEL_TYPE,
  payload,
});
export const deleteMetamodelTypeAction: (
  payload: ByIdMetamodelTypePayload
) => DeleteMetamodelTypeAction = payload => ({
  type: ActionTypes.DELETE_MODEL_TYPE,
  payload,
});

export const setIsLoadingAction: (payload: {
  is: boolean;
}) => SetStateBooleanAction = payload => ({
  type: ActionTypes.SET_IS_LOADING,
  payload,
});
export const setIsFlexibleAction: (payload: {
  is: boolean;
}) => SetStateBooleanAction = payload => ({
  type: ActionTypes.SET_IS_FLEXIBLE,
  payload,
});
export const setEditorErrorAction: (
  payload: EditorErrorPayload
) => EditorErrorAction = payload => ({
  type: ActionTypes.SET_EDITOR_ERROR,
  payload,
});
export const setSelectedNodeIdAction: (
  payload: Partial<ByIdMetamodelTypePayload>
) => SetSelectedMetamodelTypeIdAction = payload => ({
  type: ActionTypes.SET_SELECTED_NODE_ID,
  payload,
});
